<template>
  <div class="view-attendance-page">
    <md-card>
      <md-card-header class="md-card-header-icon md-card-header-primary">
        <div class="card-icon">
          <md-icon>fingerprint</md-icon>
        </div>
        <h4 class="title">Attendance Details</h4>
        <p class="category">View biometric attendance record details</p>
      </md-card-header>

      <md-card-content>
        <div v-if="isLoading" class="loading-container">
          <md-progress-spinner md-mode="indeterminate"></md-progress-spinner>
        </div>

        <div v-else-if="attendance">
          <div class="details-section">
            <h5>Employee Information</h5>
            <div class="info-grid">
              <div class="info-row">
                <div class="info-label">Employee Code:</div>
                <div class="info-value">{{ attendance.emp_code }}</div>
              </div>
              <div class="info-row">
                <div class="info-label">Employee Name:</div>
                <div class="info-value">{{ getEmployeeName(attendance.employee) }}</div>
              </div>
              <div class="info-row">
                <div class="info-label">NIDA Number:</div>
                <div class="info-value">{{ attendance.employee.nida_number || 'N/A' }}</div>
              </div>
            </div>
          </div>

          <div class="details-section">
            <h5>Attendance Information</h5>
            <div class="info-grid">
              <div class="info-row">
                <div class="info-label">Date/Time:</div>
                <div class="info-value">{{ formatDateTime(attendance.time) }}</div>
              </div>
              <div class="info-row">
                <div class="info-label">State:</div>
                <div class="info-value">
                  <badge :type="getStateBadge(attendance.state)">{{ formatState(attendance.state) }}</badge>
                </div>
              </div>
              <div class="info-row">
                <div class="info-label">Verify Type:</div>
                <div class="info-value">{{ attendance.verify_type_display || attendance.verify_type || 'N/A' }}</div>
              </div>
              <div class="info-row">
                <div class="info-label">Work Code:</div>
                <div class="info-value">{{ attendance.work_code || 'N/A' }}</div>
              </div>
            </div>
          </div>

          <div class="details-section">
            <h5>Terminal Information</h5>
            <div class="info-grid">
              <div class="info-row">
                <div class="info-label">Device:</div>
                <div class="info-value">{{ attendance.device || 'N/A' }}</div>
              </div>
              <div class="info-row">
                <div class="info-label">Terminal SN:</div>
                <div class="info-value">{{ attendance.terminal_sn || 'N/A' }}</div>
              </div>
              <div class="info-row">
                <div class="info-label">Terminal Alias:</div>
                <div class="info-value">{{ attendance.terminal_alias || 'N/A' }}</div>
              </div>
              <div class="info-row">
                <div class="info-label">Area Alias:</div>
                <div class="info-value">{{ attendance.area_alias || 'N/A' }}</div>
              </div>
            </div>
          </div>

          <div class="details-section">
            <h5>Additional Information</h5>
            <div class="info-grid">
              <div class="info-row">
                <div class="info-label">External ID:</div>
                <div class="info-value">{{ attendance.external_id || 'N/A' }}</div>
              </div>
              <div class="info-row">
                <div class="info-label">Upload Time:</div>
                <div class="info-value">{{ formatDateTime(attendance.upload_time) }}</div>
              </div>
              <div class="info-row">
                <div class="info-label">Processed:</div>
                <div class="info-value">{{ attendance.prossed ? 'Yes' : 'No' }}</div>
              </div>
            </div>
          </div>
        </div>

        <div class="action-buttons">
          <md-button @click="goBack" class="md-primary">Back to List</md-button>
          <md-button v-if="attendance.employee" @click="viewEmployee" class="md-info">
            View Employee Profile
          </md-button>
        </div>
      </md-card-content>
    </md-card>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import Badge from '@/components/Badge.vue';
import formMixin from '@/mixins/form-mixin';

export default {
  name: 'BiotimeAttendanceView',
  components: {
    Badge
  },
  mixins: [formMixin],
  data: function() {
    return {
      attendanceId: null
    };
  },
  computed: {
    ...mapGetters('biotimeAttendances', ['attendance', 'isLoading'])
  },
  created: function() {
    this.attendanceId = this.$route.params.id;
    this.fetchAttendance();
  },
  methods: {
    ...mapActions('biotimeAttendances', ['view']),

    async fetchAttendance() {
      try {
        await this.view({ id: this.attendanceId, include: 'employee' });
      } catch (error) {
        console.error('Error fetching attendance:', error);
        this.notifyError('Failed to load attendance details');
      }
    },

    getEmployeeName: function(employee) {
      if (!employee) return 'Unknown';
      var firstName = employee.first_name || '';
      var lastName = employee.last_name || '';
      var middleName = employee.middle_name || '';
      return [firstName, middleName, lastName].filter(Boolean).join(' ') || employee.emp_code || 'Unknown';
    },

    formatState: function(state) {
      var states = {
        'check_in': 'Check In',
        'check_out': 'Check Out'
      };
      return states[state] || state || 'Unknown';
    },

    getStateBadge: function(state) {
      var badges = {
        'check_in': 'success',
        'check_out': 'info'
      };
      return badges[state] || 'default';
    },

    formatDateTime: function(date) {
      if (!date) return 'N/A';
      return new Date(date).toLocaleString();
    },

    viewEmployee: function() {
      if (this.attendance.employee.id) {
        this.$router.push({ name: 'View Labourer', params: { id: this.attendance.employee.id } });
      }
    },

    goBack: function() {
      this.$router.push({ name: 'List Biotime Attendances' });
    }
  }
};
</script>

<style scoped>
.view-attendance-page {
  padding: 20px;
}

.loading-container {
  display: flex;
  justify-content: center;
  padding: 40px;
}

.details-section {
  margin-bottom: 24px;
  padding-bottom: 15px;
  border-bottom: 1px solid #e0e0e0;
}

.details-section h5 {
  margin: 0 0 15px 0;
  color: #9c27b0;
  font-size: 16px;
}

.info-grid {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.info-row {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
}

.info-label {
  width: 150px;
  font-weight: 500;
  color: #666;
}

.info-value {
  flex: 1;
  color: #333;
}

.action-buttons {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 20px;
  padding-top: 15px;
  border-top: 1px solid #e0e0e0;
}

@media (max-width: 768px) {
  .view-attendance-page {
    padding: 10px;
  }

  .info-label {
    width: 120px;
    font-size: 13px;
  }

  .info-value {
    font-size: 13px;
  }
}
</style>